import { useLayoutEffect, useRef, useState } from 'react';

import { createPortal } from 'react-dom';

import PropTypes from 'prop-types';

import { LAYER } from '#lib/constants/layers';

const GAP = 8;
const VIEWPORT_MARGIN = 8;

/**
 * Hover tooltip rendered through a portal onto document.body with fixed positioning, so the
 * panel escapes any overflow container (e.g. a scrollable Modal) and is always shown in full.
 * Once the panel size is known it is clamped to the viewport horizontally, and a panel that would run
 * past the bottom edge opens above its trigger instead.
 */
export default function Tooltip({ children, content, position = 'bottom-left', className = 'inline-flex' }) {
	const [anchor, setAnchor] = useState(null); // trigger rect while the tooltip is open
	const [style, setStyle] = useState(null);
	const panelRef = useRef(null);

	// Position the panel once its size is measurable; useLayoutEffect runs before paint, so
	// the hidden first frame is never visible.
	useLayoutEffect(() => {
		if (!anchor || !panelRef.current) return;
		const panel = panelRef.current.getBoundingClientRect();

		let top = position === 'right' ? anchor.top : anchor.bottom + GAP;
		if (top + panel.height > window.innerHeight - VIEWPORT_MARGIN) {
			const above = position === 'right' ? anchor.bottom - panel.height : anchor.top - GAP - panel.height;
			top = Math.max(VIEWPORT_MARGIN, above);
		}
		let left = anchor.left;
		if (position === 'bottom-right') left = anchor.right - panel.width;
		if (position === 'right') left = anchor.right + GAP;

		// Clamp horizontally so the full content stays inside the viewport.
		left = Math.max(VIEWPORT_MARGIN, Math.min(left, window.innerWidth - panel.width - VIEWPORT_MARGIN));

		setStyle({ top, left });
	}, [anchor, position]);

	if (!content) return children;

	const show = (e) => setAnchor(e.currentTarget.getBoundingClientRect());
	const hide = () => {
		setAnchor(null);
		setStyle(null);
	};

	return (
		<div className={`relative ${className}`} onMouseEnter={show} onMouseLeave={hide}>
			{children}
			{anchor &&
				createPortal(
					<div
						ref={panelRef}
						className={`fixed ${LAYER.tooltip}`}
						style={style ?? { top: 0, left: 0, visibility: 'hidden' }}
					>
						{content}
					</div>,
					document.body,
				)}
		</div>
	);
}

Tooltip.propTypes = {
	children: PropTypes.node.isRequired,
	content: PropTypes.node,
	position: PropTypes.oneOf(['bottom-left', 'bottom-right', 'right']),
	className: PropTypes.string,
};
